import styled from "styled-components";
import { Link } from "react-router-dom";
import { ArrowLeft } from "react-feather";

// Paleta extraída do logo real (lupa ciana sobre fundo branco, wordmark preto,
// cabo grafite com detalhe vermelho) — navy claro em vez do "espaço" quase-preto
// que o site usava antes. Mesmos tokens replicados em HomePage.js e Header.jsx.
export const SitePage = styled.main`
  min-height: calc(100vh - 92px);
  padding: 48px 20px 88px;
  background:
    radial-gradient(circle at top left, rgba(34, 184, 209, 0.09), transparent 30%),
    radial-gradient(circle at bottom right, rgba(108, 143, 176, 0.08), transparent 26%),
    #17253D;
  color: #E7EDF7;
`;

export const SiteShell = styled.div`
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  display: grid;
  gap: 24px;
`;

export const Surface = styled.section`
  border-radius: 16px;
  border: 1px solid rgba(196, 208, 228, 0.12);
  background: #1F3050;
  box-shadow: none;
`;

export const SurfaceBody = styled.div`
  padding: ${({ $compact }) => ($compact ? "22px 20px" : "30px 26px")};

  @media (max-width: 768px) {
    padding: ${({ $compact }) => ($compact ? "20px 18px" : "24px 20px")};
  }
`;

// Botão "Voltar" padrão do site: usado em todas as páginas para manter o
// mesmo visual e comportamento de navegação (ícone + texto, mesma cor/hover).
const StyledBackLink = styled(Link)`
  width: fit-content;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #93A0BE;
  text-decoration: none;
  font-weight: 700;
  font-size: 0.9rem;
  transition: color 0.18s ease, transform 0.18s ease;

  &:hover {
    color: #22B8D1;
    transform: translateX(-2px);
  }

  svg {
    flex: none;
  }
`;

export function BackLink({ children = "Voltar", ...props }) {
  return (
    <StyledBackLink {...props}>
      <ArrowLeft size={16} />
      {children}
    </StyledBackLink>
  );
}

// Variante em botão (não navega por rota) para "voltar" que só troca estado
// local, como fechar um leitor/visualizador embutido na própria página.
const StyledBackButtonAction = styled.button`
  width: fit-content;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  background: transparent;
  padding: 0;
  color: #93A0BE;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: color 0.18s ease, transform 0.18s ease;

  &:hover {
    color: #22B8D1;
    transform: translateX(-2px);
  }

  svg {
    flex: none;
  }
`;

export function BackButtonAction({ children = "Voltar", ...props }) {
  return (
    <StyledBackButtonAction type="button" {...props}>
      <ArrowLeft size={16} />
      {children}
    </StyledBackButtonAction>
  );
}

export const PageHeader = styled.div`
  display: grid;
  gap: 14px;
  align-content: start;
`;

export const Eyebrow = styled.span`
  display: inline-flex;
  width: fit-content;
  min-height: 30px;
  align-items: center;
  padding: 0 12px;
  border-radius: 999px;
  background: ${({ $tone }) =>
    $tone === "success"
      ? "rgba(34, 184, 209, 0.12)"
      : $tone === "violet"
        ? "rgba(108, 143, 176, 0.16)"
        : "rgba(34, 184, 209, 0.12)"};
  border: 1px solid
    ${({ $tone }) =>
      $tone === "success"
        ? "rgba(34, 184, 209, 0.28)"
        : $tone === "violet"
          ? "rgba(108, 143, 176, 0.30)"
          : "rgba(34, 184, 209, 0.28)"};
  color: ${({ $tone }) =>
    $tone === "success" ? "#67D6E7" : $tone === "violet" ? "#AEC3DA" : "#67D6E7"};
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
`;

export const PageTitle = styled.h1`
  margin: 0;
  color: #F8FBFF;
  font-family: "Bitter", serif;
  font-weight: 700;
  font-size: clamp(2.2rem, 4vw, 3.7rem);
  line-height: 1.06;
  letter-spacing: 0;
  max-width: ${({ $max }) => $max || "760px"};
`;

export const PageIntro = styled.p`
  margin: 0;
  max-width: ${({ $max }) => $max || "760px"};
  color: #C7D2E8;
  line-height: 1.72;
  font-size: 1.02rem;
`;

export const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const actionBase = `
  min-height: 46px;
  padding: 0 18px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-decoration: none;
  font-weight: 800;
  transition: transform 0.18s ease, border-color 0.18s ease, background 0.18s ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

export const PrimaryLink = styled(Link)`
  ${actionBase}
  background: #22B8D1;
  color: #0A1424;
`;

export const SecondaryLink = styled(Link)`
  ${actionBase}
  background: rgba(34, 184, 209, 0.08);
  border: 1px solid rgba(34, 184, 209, 0.28);
  color: #DFF6FA;
`;

export const PrimaryExternalLink = styled.a`
  ${actionBase}
  background: #22B8D1;
  color: #0A1424;
`;

export const AccentExternalLink = styled.a`
  ${actionBase}
  background: linear-gradient(90deg, #22B8D1 0%, #0B7F93 100%);
  color: #0A1424;
  box-shadow: 0 18px 34px rgba(34, 184, 209, 0.20);
`;

export const Grid = styled.section`
  display: grid;
  grid-template-columns: ${({ $columns }) => $columns || "repeat(3, minmax(0, 1fr))"};
  gap: ${({ $gap }) => $gap || "18px"};

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

export const Card = styled.article`
  border-radius: 14px;
  border: 1px solid ${({ $accent }) => $accent || "rgba(196, 208, 228, 0.12)"};
  background: ${({ $muted }) => ($muted ? "rgba(10, 17, 30, 0.34)" : "#1F3050")};
  box-shadow: ${({ $featured }) =>
    $featured ? "0 18px 42px rgba(34, 184, 209, 0.12)" : "none"};
  padding: ${({ $compact }) => ($compact ? "18px" : "24px 22px")};
  display: grid;
  gap: 12px;
`;

export const IconWrap = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${({ $tone }) =>
    $tone === "success"
      ? "rgba(34, 184, 209, 0.12)"
      : $tone === "violet"
        ? "rgba(108, 143, 176, 0.16)"
        : "rgba(34, 184, 209, 0.12)"};
  border: 1px solid
    ${({ $tone }) =>
      $tone === "success"
        ? "rgba(34, 184, 209, 0.24)"
        : $tone === "violet"
          ? "rgba(108, 143, 176, 0.28)"
          : "rgba(34, 184, 209, 0.24)"};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ $tone }) =>
    $tone === "success" ? "#22B8D1" : $tone === "violet" ? "#9FB6D6" : "#22B8D1"};
`;

export const CardTitle = styled.h2`
  margin: 0;
  color: #F8FBFF;
  font-family: "Bitter", serif;
  font-weight: 700;
  font-size: 1.08rem;
`;

export const CardText = styled.p`
  margin: 0;
  color: #C7D2E8;
  line-height: 1.66;
  font-size: 0.96rem;
`;

export const StatBlock = styled.div`
  padding: 18px;
  border-radius: 12px;
  background: rgba(10, 17, 30, 0.38);
  border: 1px solid rgba(196, 208, 228, 0.16);
  display: grid;
  gap: 8px;
`;

export const StatLabel = styled.span`
  color: ${({ $tone }) => ($tone === "success" ? "#67D6E7" : "#67D6E7")};
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
`;

export const StatText = styled.p`
  margin: 0;
  color: #E7EDF7;
  line-height: 1.62;
`;
